import { ENDPOINT, ERRORS } from 'src/constants';
import fetch from 'isomorphic-fetch';
import {mergeDeep} from './util';

const COMMON_FETCH_OPTIONS = {
    headers: {
        'Content-Type': 'application/json',
        'Connection': 'keep-alive'
    },
    referrerPolicy: 'origin-when-cross-origin',
    mode: 'same-origin',
    credentials: 'same-origin',
    cache: 'no-cache',
    redirect: 'follow',
};

/**
 * @deprecated in favor of fetchJsonNoLogger - ToDo: remove in next major release
 * Internal implementation of fetch sending a JSON payload to the specified endpoint used
 * as the preferred method with keepalive set to true to help send data after page transitions
 *
 * @param payload {Object} - analytics data payload
 * @param logger {Function} - Check logErrorWithEmit on EgClickstream.
 * @param fetchOptions {Object} - options to control the behavior of fetch request, example: {keepalive: true}, or {}
 */
function fetchJson(method, payload, logger, fetchOptions) {
    const options = Object.assign({}, COMMON_FETCH_OPTIONS, {
        method: method,
        body: JSON.stringify(payload),
        keepalive: !!(fetchOptions && fetchOptions.keepalive)
    });
    fetch(ENDPOINT, options).then((res)=>{
        if(res.status >= 400){
            logger(new Error(`Error on fetch ${ENDPOINT} with ${res.status} error code.`), ERRORS.FETCH_ERROR);
        }
    }).catch((e)=>{
        logger(new Error(`Error on fetch ${ENDPOINT}. ${e}`), ERRORS.FETCH_ERROR);
    });
}

/**
 * Internal implementation of fetch sending a JSON payload to the specified endpoint used
 * as the preferred method with keepalive set to true to help send data after page transitions
 *
 * @param payload {Object} - analytics data payload
 * @param fetchOptions {Object} - options to control the behavior of fetch request, example: {keepalive: true}, or {}
 */
async function fetchJsonNoLogger(method, payload, fetchOptions) {
    const options = mergeDeep({}, COMMON_FETCH_OPTIONS, {
        method: method,
        body: JSON.stringify(payload),
        keepalive: !!(fetchOptions && fetchOptions.keepalive),
        headers: (fetchOptions && fetchOptions.headers) || {}
    });
    return await fetch(ENDPOINT, options);
}

const MAX_FETCH_RETRIES = 2;
const FETCH_RETRY_DELAY = 100;
const KEEPALIVE_RELATED_ERRORS = [
    'Failed to fetch',
    'Load failed',
    'Reached maximum amount of queued data of 64Kb for keepalive requests'
];

function isKeepaliveRelated(e) {
    return e instanceof Error && !!e.message && KEEPALIVE_RELATED_ERRORS.some(regex => e.message.includes(regex));
}

async function fetchJsonWithRetry(method, payload, fetchOptions) {
    let retries = 0;
    while (retries <= MAX_FETCH_RETRIES) {
        try {
            // Disable keepalive for retries
            if (retries > 0) {
                fetchOptions.keepalive = false;
            }

            const response = await fetchJsonNoLogger(method, payload, fetchOptions);

            // reject if response status is 4xx or 5xx
            if (response.status >= 400) {
                return Promise.reject(new Error(`Error on fetch ${ENDPOINT}. Failed with ${response.status} error code`));
            }

            // Collector service might not respond with anything which is valid
            const jsonString = await response.text();
            const json = jsonString === "" ? {} : response.json();

            // resolve if response status is 2xx
            return json;
        } catch (e) {
            // reject if max retries reached
            if (retries >= MAX_FETCH_RETRIES) {
                return Promise.reject(new Error(`Error on fetch ${ENDPOINT}. ${e}. Max retries reached`));
            }

            // retry if keepalive related error occurred, otherwise reject
            if (isKeepaliveRelated(e)) {
                retries++;
                await new Promise(resolve => setTimeout(resolve, FETCH_RETRY_DELAY));
            } else {
                return Promise.reject(new Error(`Error on fetch ${ENDPOINT}. ${e}`));
            }
        }
    }
}

function getViewId(event) {
    return event && event.identifiers && event.identifiers.view_id;
}

export class Helpers {
    /**
     * Log the error message
     *
     * @param {Error} error
     * @param prefix - prefix to identify error messages as part of a library
     * @param {object} logger
     */
    static logError(error, prefix = 'EgClickstream', logger = console) {
        if (!error) {
            return;
        }

        logger.error(`${prefix} - ${error.toString()}`);
    }

    /**
     * @deprecated in favor of postJsonWithRetry - ToDo: remove in next major release
     * Determine if fetch or xhr should be used to post
     *
     * @param {Object} payload - analytics data payload
     * @param {Function} logger - Check logErrorWithEmit on EgClickstream.
     * @param {Object} [fetchOptions] - optional - options to control the behavior of fetch request
     */
    static postJson(payload, logger = console.error, fetchOptions={keepalive: false}) {
        return fetchJson('POST', payload, logger, fetchOptions);
    }

    /**
     * Post JSON with retry mechanism to handle keepalive related errors
     *
     * @param {Object} payload - analytics data payload
     * @param {Object} [fetchOptions] - optional - options to control the behavior of fetch request
     */
    static postJsonWithRetry(payload, fetchOptions={keepalive: false}) {
        return fetchJsonWithRetry('POST', payload, fetchOptions);
    }

    /**
     * Determine if the error is related to keepalive
     * @param {*} error - Error object
     * @returns {boolean} - Returns true if the error is related to keepalive
     */
    static isKeepaliveRelated(error) {
        return error instanceof Error && !!error.message && KEEPALIVE_RELATED_ERRORS.some(regex => error.message.includes(regex));
    }

    /**
     * Chunk a list of event objects by view_id
     * start a new chunk when the view_id changes
     * @param {Array} events - list of event objects
     * @returns {Array} - list of event objects list grouped by view_id
     */
    static groupEventsByViewId(events) {
        const result = {};

        events.forEach(event => {
            const viewId = getViewId(event) || 'no_view_id';
            if (result[viewId]) {
                result[viewId].push(event);
            } else {
                result[viewId] = [event];
            }
        });

        return Object.keys(result).map((key) => result[key]);
    }

    /**
     * Get view ID from an event object
     * @param {Object} event - event object
     * @returns {string} - view ID
     */
    static getViewId(event) {
        return getViewId(event);
    }

    /**
     * Get header value from event payload
     * @param {Array} events - list of event objects
     */
    static getHeaderValue(events) {
        // get ctx-view-id from the first event
        const viewId = events && events.length > 0 ? getViewId(events[0]) : undefined;
        const headers = {};

        if (viewId) {
            headers['ctx-view-id'] = viewId;
        }

        return headers;
    }
}